import {DateTime} from "luxon"
import { DOMINICA_TIMEZONE, DAY_TIME_HOUR_LIST } from "../constants/datetime"
export const timestampToDateString = (timestamp, format='yyyy-MM-dd', useLocalTimezone=true, timezone=DOMINICA_TIMEZONE) => {
    const options = {locale: "en-US"} 
    if (!useLocalTimezone) options.zone = timezone
    return DateTime.fromMillis(timestamp, options).toFormat(format)
}

export const dateStringToTimestamp = (dateString, format='yyyy-MM-dd', useLocalTimezone=true, timezone=DOMINICA_TIMEZONE) => {
    const options = {locale: "en-US"} 
    if (!useLocalTimezone) options.zone = timezone
    return DateTime.fromFormat(dateString, format, options).toMillis()
}

export const timestampsAreOnSameDay = (t1, t2) => {
    if (typeof t1 !== "number" || typeof t2 !== "number") return false  
    const dt1 = DateTime.fromMillis(t1)
    const dt2 = DateTime.fromMillis(t2)
    return dt1.year === dt2.year &&
           dt1.month === dt2.month &&
           dt1.day === dt2.day
}

export const getDateString = (timestamp = Date.now()) => {
    const now = new Date(timestamp)
    const year = now.getFullYear()
    const month = now.getMonth()
    const day = now.getDate()
    return `${year}-${month}-${day}`
}

export const getTimestampForStartOfDay = (timestamp=Date.now(), useLocalTimezone=true, timezone=DOMINICA_TIMEZONE) => {
    const options = {locale: "en-US"} 
    if (!useLocalTimezone) options.zone = timezone
    const dt = DateTime.fromMillis(timestamp, options)
    return dt.startOf('day').ts
}

export const getTimestampForStartOfMonth = (timestamp=Date.now(), useLocalTimezone=true, timezone=DOMINICA_TIMEZONE) => {
    const options = {locale: "en-US"} 
    if (!useLocalTimezone) options.zone = timezone
    const dt = DateTime.fromMillis(timestamp, options)
    return dt.startOf('month').ts
}

//Gets the day of the week
export const getWeekday = (timestamp, numeric = false, timezone = DOMINICA_TIMEZONE) => {
    const dateTime = DateTime.fromMillis(timestamp, { zone: timezone });
    return numeric ? dateTime.weekday : dateTime.toFormat("cccc").toLowerCase();
};

//Converts a "HH:mm" time string to a Unix timestamp.
export const parseTime = (timeStr, referenceTimestamp, timezone, dayOffset = 0) => {
    if (typeof timeStr !== "string" || !/^\d{2}:\d{2}$/.test(timeStr)) {
        console.warn(`Invalid time format: ${timeStr}`);
        return null; // Return null for invalid inputs
    }
    
    const [hours, minutes] = timeStr.split(":").map(Number);
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
        console.warn(`Invalid time values: ${timeStr}`);
        return null;
    }

    const referenceDate = DateTime.fromMillis(referenceTimestamp, { zone: timezone });
    return referenceDate
        .plus({ days: dayOffset })
        .set({ hour: hours, minute: minutes, second: 0, millisecond: 0 })
        .toMillis();
};

//Adds a specified number of days to a timestamp
export const addDays = (timestamp, days, timezone = DOMINICA_TIMEZONE) => {
    return DateTime.fromMillis(timestamp, { zone: timezone }).plus({ days }).toMillis();
};

// Adds a time duration (e.g., "00:30") from a timestamp
export const addDuration = (timestamp, durationStr, timezone = DOMINICA_TIMEZONE) => {
    if (
        typeof durationStr !== "string" ||
        !/^\d{1,2}:\d{2}$/.test(durationStr)
      ) {
        // Invalid or missing duration string — return original timestamp
        return timestamp;
      }
    const [hours, minutes] = durationStr.split(":").map(Number);
    return DateTime.fromMillis(timestamp, { zone: timezone }).plus({ hours, minutes }).toMillis();
};

// Subtracts a time duration (e.g., "00:30") from a timestamp
export const subtractDuration = (timestamp, durationStr, timezone = DOMINICA_TIMEZONE) => {
    if (
        typeof durationStr !== "string" ||
        !/^\d{1,2}:\d{2}$/.test(durationStr)
      ) {
        // Invalid or missing duration string — return original timestamp
        return timestamp;
      }
    const [hours, minutes] = durationStr.split(":").map(Number);
    return DateTime.fromMillis(timestamp, { zone: timezone }).minus({ hours, minutes }).toMillis();
};

function isValidDuration(timeStr) {
    if (typeof timeStr !== "string") return false
    const regex = /^(\d{1,2}):([0-5][0-9])$/;
    return regex.test(timeStr);
}
//
export const addDurations = (dStr1, dStr2) => {
    if (
        !isValidDuration(dStr1) ||
        !isValidDuration(dStr2)
    ) {
        return dStr1
    }
    const [hr1, min1] = dStr1.split(":").map(Number);
    const [hr2, min2] = dStr2.split(":").map(Number);
    let mins = min1 + min2
    let hours = hr1 + hr2 
    if (mins >= 60) {
        //if there are 60 or more minutes, then add them to the hours variable
        //find the hours
        const addedHours = Math.floor(mins / 60);
        //remove the hours from the minutes
        mins = mins % 60;
        //add the hours to the previous hours
        hours = hours + addedHours
    }

    const addOptionalZero = num => `${num < 10 ? `0${num}`: num}`
    return `${addOptionalZero(hours)}:${addOptionalZero(mins)}`
}

export const parseHourMinute = (timeStr) => {
    const regex = /^(\d{1,2}):([0-5][0-9])$/;
    const match = timeStr.match(regex);
    
    if (!match) {
      console.warn("Invalid HH:MM format");
      return {days: 0, hrs: 0, mins:0}
    }
  
    let hrs = parseInt(match[1], 10);
    let mins = parseInt(match[2], 10);
  
    const days = Math.floor(hrs / 24);
    hrs = hrs % 24;
  
    return { days, hrs, mins };
}
  
export const isBefore = (timestamp1, timestamp2) => {
    return timestamp1 < timestamp2;
};

export const isAfter = (timestamp1, timestamp2) => {
    return timestamp1 > timestamp2;
};

export const convert24HrTimeFormatTo12HrTime = (time) => {
    if (typeof time !== "string" || !/^\d{2}:\d{2}$/.test(time)) {
        console.warn(`Invalid time format: ${time}`);
        return time;
    }
    
    const fragments = time.split(":");
    if (fragments.length !== 2) return time;

    let hours = Number(fragments[0]);
    let minutes = fragments[1];
    let period = hours >= 12 ? "PM" : "AM";

    if (hours === 0) {
        hours = 12; // Midnight case
    } else if (hours > 12) {
        hours -= 12; // Convert 24-hour to 12-hour format
    }

    return `${hours}:${minutes} ${period}`;
};

export const getRelativeWeekdayString = (
    timestamp,
    referenceTimestamp = Date.now(),
    format = "EEEE", // Default format is full weekday name (e.g., "Monday")
    timezone = DOMINICA_TIMEZONE
) => {
    const startOfToday = getStartOfDay(referenceTimestamp, timezone);
    const startOfTomorrow = addDays(startOfToday, 1, timezone);
    const startOfYesterday = addDays(startOfToday, -1, timezone);
    
    if (isSameDay(timestamp, startOfToday, timezone)) return "today";
    if (isSameDay(timestamp, startOfTomorrow, timezone)) return "tomorrow";
    if (isSameDay(timestamp, startOfYesterday, timezone)) return "yesterday";
    
    return DateTime.fromMillis(timestamp, { zone: timezone }).toFormat(format).toLowerCase();
};

export const isSameDay = (timestamp1, timestamp2, timezone) => {
    return getStartOfDay(timestamp1, timezone) === getStartOfDay(timestamp2, timezone);
};

export const getStartOfDay = (timestamp, timezone) => {
    return DateTime.fromMillis(timestamp, { zone: timezone }).startOf("day").toMillis();
};

/**
 * Purpose: used to keep a time interval valid by ensuring the fromTime is before the toTime and viceversa
 */
 export const intervalTimeChangeIsValid = (newTime, isFromTime, otherTime, crossesMidnight=false) => {
    //find the new and other time's position in the time list
    const timeList = DAY_TIME_HOUR_LIST.map(t => t.id)
    const newIndex = timeList.indexOf(newTime)
    const otherIndex = timeList.indexOf(otherTime)
    //catch invalid times
    if (newIndex === -1 || otherIndex === -1) return false
    if (!crossesMidnight) {
        /** this is the regular set up where all opening hours fall on the same day **/

        //if it is the fromTime, then it must be earlier than the toTime, not later or at the same time
        //if it is the toTime, then it must be later than the fromTime, not earlier or at the same time
        return isFromTime ? newIndex < otherIndex : newIndex > otherIndex
    } else {
        /** This is the "crosses midnight" setup, for restaurants that go from 4PM to 2 AM for example */

        // Allow any values unless they are equal 
        return newTime !== otherTime
    }
}